import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronRight, LogOut, LayoutDashboard, School as SchoolIcon, User, ArrowRight } from 'lucide-react';
import logo from '../../assets/school-logo-header.png';
import { useAuth } from '../../contexts/AuthContext';

export const Navbar: React.FC = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const { user, school, isAdmin, logout } = useAuth();

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (isMobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isMobileMenuOpen]);

    const navLinks = [
        { name: 'Home', path: '/' },
        { name: 'Events', path: '/events' },
        { name: 'Code of Conduct', path: '/code-of-conduct' },
        { name: 'Instructions', path: '/instructions' },
        { name: 'Venue Map', path: '/venue-map' },
    ];

    const isActive = (path: string) => location.pathname === path;

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/');
        } catch (error) {
            console.error('Logout failed', error);
        }
    };

    const getPanelLink = () => {
        if (isAdmin) return '/admin/dashboard';
        if (school) return '/school/dashboard';
        return '/login';
    };

    return (
        <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
            isScrolled || isMobileMenuOpen
                ? 'bg-[#0A0A0A]/95 backdrop-blur-xl border-b border-white/5'
                : 'bg-transparent'
        }`}>
            <div className="max-w-[1400px] mx-auto px-6">
                <div className="flex items-center justify-between h-20">
                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-3" onClick={() => setIsMobileMenuOpen(false)}>
                        <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 p-1.5">
                            <img src={logo} alt="Logo" className="w-full h-full object-contain" />
                        </div>
                        <div className="flex items-baseline gap-1">
                            <span className="text-xl font-display font-bold text-white">ENIGMA</span>
                            <span className="text-sm font-display font-bold text-[#FF991C]">'26</span>
                        </div>
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden lg:flex items-center gap-1">
                        {navLinks.map((link) => (
                            <Link
                                key={link.path}
                                to={link.path}
                                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                                    isActive(link.path)
                                        ? 'text-white bg-white/10'
                                        : 'text-white/50 hover:text-white hover:bg-white/5'
                                }`}
                            >
                                {link.name}
                            </Link>
                        ))}
                    </div>

                    {/* Desktop Auth */}
                    <div className="hidden lg:flex items-center gap-3">
                        {user ? (
                            <>
                                <div className="text-right mr-2">
                                    <p className="text-xs text-white/40">Logged in as</p>
                                    <p className="text-sm font-medium text-[#FF991C]">
                                        {isAdmin ? 'Admin' : school?.school_name || 'School'}
                                    </p>
                                </div>
                                <Link to={getPanelLink()}>
                                    <button className="px-4 py-2 text-sm font-medium text-white bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition-all">
                                        {isAdmin ? <LayoutDashboard className="w-4 h-4 inline mr-2" /> : <SchoolIcon className="w-4 h-4 inline mr-2" />}
                                        Panel
                                    </button>
                                </Link>
                                <button
                                    onClick={handleLogout}
                                    className="p-2 text-white/50 hover:text-[#EF4444] hover:bg-[#EF4444]/10 rounded-lg transition-all"
                                >
                                    <LogOut className="w-5 h-5" />
                                </button>
                            </>
                        ) : (
                            <Link to="/login">
                                <button className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-black bg-[#FF991C] rounded-xl hover:bg-[#FF991C]/90 transition-all">
                                    Login
                                    <ArrowRight className="w-4 h-4" />
                                </button>
                            </Link>
                        )}
                    </div>

                    {/* Mobile Menu Button */}
                    <button
                        className="lg:hidden w-10 h-10 flex items-center justify-center text-white rounded-lg hover:bg-white/5"
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    >
                        {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>
            </div>

            {/* Mobile Menu */}
            <div className={`lg:hidden fixed inset-0 top-20 bg-[#0A0A0A] z-50 transition-transform duration-300 ${
                isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
            }`}>
                <div className="p-6 space-y-2">
                    {navLinks.map((link) => (
                        <Link
                            key={link.path}
                            to={link.path}
                            onClick={() => setIsMobileMenuOpen(false)}
                            className={`flex items-center justify-between p-4 rounded-xl transition-all ${
                                isActive(link.path)
                                    ? 'bg-[#FF991C]/10 border border-[#FF991C]/20 text-white'
                                    : 'bg-white/[0.02] border border-white/5 text-white/60 hover:text-white'
                            }`}
                        >
                            <span className="font-medium">{link.name}</span>
                            <ChevronRight className="w-5 h-5" />
                        </Link>
                    ))}

                    <div className="pt-6 mt-6 border-t border-white/5 space-y-3">
                        {user ? (
                            <>
                                <div className="px-4 py-2">
                                    <p className="text-xs text-white/40 mb-1">Logged in as</p>
                                    <p className="text-sm font-semibold text-[#FF991C]">
                                        {isAdmin ? 'Administrator' : school?.school_name}
                                    </p>
                                </div>
                                <Link to={getPanelLink()} onClick={() => setIsMobileMenuOpen(false)}>
                                    <button className="w-full p-4 text-left font-medium text-white bg-white/5 border border-white/10 rounded-xl">
                                        Go to Panel →
                                    </button>
                                </Link>
                                <button
                                    onClick={() => { handleLogout(); setIsMobileMenuOpen(false); }}
                                    className="w-full p-4 text-left font-medium text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/20 rounded-xl"
                                >
                                    Logout
                                </button>
                            </>
                        ) : (
                            <Link to="/login" onClick={() => setIsMobileMenuOpen(false)}>
                                <button className="w-full p-4 text-center font-semibold text-black bg-[#FF991C] rounded-xl">
                                    Login to Portal
                                </button>
                            </Link>
                        )}
                    </div>
                </div>
            </div>
        </nav>
    );
};
